import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { useAuth, api } from '../../context/AuthContext';

const TEAM = ['admin', 'kanzlei_mitarbeiter', 'freelancer'];
const BEREICH = /^\/(admin|team|kanzlei|team-portal)/;
export const sucheOeffnen = () => window.dispatchEvent(new Event('kanzlei-suche'));

const useTreffer = (q) => {
  const [gruppen, setGruppen] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setGruppen([]); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get('/kanzlei/suche', { params: { q } }).then((r) => setGruppen(r.data.gruppen || [])).catch(() => setGruppen([])).finally(() => setBusy(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);
  return { gruppen, busy };
};

export const GlobalSuche = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { gruppen, busy } = useTreffer(q);
  const aktiv = TEAM.includes(user?.role) && BEREICH.test(pathname);

  useEffect(() => {
    if (!aktiv) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (e.key === '/' && !['input', 'textarea', 'select'].includes(tag) && !e.target?.isContentEditable) { e.preventDefault(); setOpen(true); }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('kanzlei-suche', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('kanzlei-suche', onOpen); };
  }, [aktiv]);

  if (!aktiv) return null;
  const go = (link) => { setOpen(false); setQ(''); navigate(link); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 max-w-2xl bg-[#0B1724]/95 backdrop-blur-xl border border-[#b8893d]/40 text-white" data-testid="globale-suche">
        <DialogTitle className="sr-only">Suche im Kanzlei-Bereich</DialogTitle>
        <Command shouldFilter={false} className="bg-transparent text-white [&_[cmdk-group-heading]]:text-[#e0b56a] [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:tracking-[0.25em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-input]]:h-14 [&_[cmdk-item]]:py-2.5">
          <CommandInput value={q} onValueChange={setQ} placeholder="Mandant, Aktenzeichen, Rückruf, Aufgabe, Dokument …" className="text-white placeholder:text-white/40" data-testid="globale-suche-input" />
          <CommandList className="max-h-[60vh]">
            {busy && <div className="px-4 py-3 text-xs text-white/60 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Suche …</div>}
            {!busy && q.trim().length >= 2 && <CommandEmpty className="py-6 text-center text-sm text-white/60">Keine Treffer</CommandEmpty>}
            {gruppen.map((g) => (
              <CommandGroup key={g.titel} heading={g.titel} data-testid={`suche-gruppe-${g.titel.toLowerCase()}`}>
                {g.treffer.map((t, i) => (
                  <CommandItem key={`${g.titel}-${i}`} value={`${g.titel}-${i}-${t.titel}`} onSelect={() => go(t.link)} className="flex flex-col items-start gap-0.5 rounded-lg text-white data-[selected=true]:bg-white/10 data-[selected=true]:text-white" data-testid="suche-treffer">
                    <span className="text-sm text-white">{t.titel}</span>
                    {t.sub && <span className="text-[11px] text-white/60">{t.sub}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
          <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 tracking-wider">↑ ↓ wählen · Enter öffnen · Esc schließen</div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
